import styled from '@emotion/styled'
import {
  Card,
  CardContent,
  CardMedia,
  Stack,
  Typography,
  TypographyProps
} from '@mui/material'
import React from 'react'

import { getUrlByDomain } from '@js/common/getUrlByDomain'

import { LinkWrapperWithWT } from '@f-fertighaus/applications/Next/components/common/LinkWrapper'
import { t } from '@f-fertighaus/applications/Next/public/locales/de'
import { HouseDetail } from '@f-fertighaus/applications/Next/types/houseDetailsResponse'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { PRICE } from '@f-fertighaus/applications/Next/utils/price'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'

import {
  HOUSE_CARD_TILE_HEIGHT,
  HOUSE_CARD_TILE_WIDTH_PX
} from '../constants/Constants'

interface HouseTileProps {
  item: HouseDetail
}

const SquareMetersLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant='caption'
    fontWeight='fontWeightBold'
    sx={{ minWidth: '50px', textAlign: 'right' }}>
    {children}
    {' m'}
    <sup>{'2'}</sup>
  </Typography>
)

const PriceInEuroLabel = ({ children }: { children: number | undefined }) => {
  return (
    <Typography variant='caption' fontWeight='fontWeightBold'>
      {children
        ? t('houseDetailPage.houseOverview.price', {
            price: PRICE.formatPrice(children)
          })
        : t('houseDetailPage.houseOverview.price-missing')}
    </Typography>
  )
}

const Caption = styled((props: TypographyProps) => (
  <Typography {...props} variant='caption' />
))`
  color: ${({ theme }) => theme.palette.stone[500]};
`

const StyledCard = styled(Card)`
  width: ${HOUSE_CARD_TILE_WIDTH_PX}px;
  height: ${HOUSE_CARD_TILE_HEIGHT}px;
  display: flex;
  flex-direction: column;
  border-radius: var(--border-radius-card-medium);
  box-shadow: 0 5px 10px -1px rgb(59 71 104 / 5%), 0 2px 4px -3px #3b4768,
    0 2px 40px 0 rgb(59 71 104 / 5%);
  background-color: ${({ theme }) => theme.palette.background.paper};
`

const StyledCardContent = styled(CardContent)`
  padding: ${({ theme }) => theme.spacing(2)};
  flex-grow: 1;

  &:last-child {
    padding-bottom: 16px;
  }
`

const ImageWrapper = styled.div`
  width: 50%;
  max-height: 32px;
  padding-left: ${({ theme }) => theme.spacing(0.5)};
  text-align: end;
`

export const HouseCard = React.memo(({ item }: HouseTileProps) => {
  const [displayedExteriorImage] = item.exterior_images
  const { domain } = useHouseDetail()
  const getUrl = getUrlByDomain(domain)

  React.useEffect(() => {
    webtrack({
      category: 'item-collection',
      action: 'display',
      label: 'other-houses-card',
      location: 'vendor-section',
      values: [{ type: 'house_nid', value: item.nid }]
    })
  }, [item.nid])

  return (
    <LinkWrapperWithWT
      url={getUrl('houseDetail')(item.slug, item.vendor_verbose.url)}
      wtPayload={{
        category: 'item',
        action: 'click',
        label: 'other-houses',
        location: 'vendor-section',
        values: [{ type: 'house_nid', value: item.nid }]
      }}>
      <StyledCard data-sel='crd-house-dflt'>
        <CardMedia
          component='img'
          alt={displayedExteriorImage.alt_text}
          src={displayedExteriorImage['fill-320x240']}
          style={{ width: '100%' }}
        />
        <StyledCardContent>
          <Stack justifyContent='space-between' sx={{ height: '100%' }}>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='flex-start'>
              <Stack>
                <Typography
                  gutterBottom
                  variant='body2'
                  fontWeight='fontWeightBold'
                  color={theme => theme.palette.text.primary}
                  sx={{ marginBottom: 0, lineHeight: 1 }}>
                  {item.name}
                </Typography>
                <Caption>{item.vendor_verbose.display_name}</Caption>
              </Stack>
              <ImageWrapper>
                <img
                  src={
                    item.vendor_verbose.logo.medium ||
                    item.vendor_verbose.logo['max-320x240.noauto']
                  }
                  alt='vendor-logo'
                  style={{ maxHeight: '32px' }}
                />
              </ImageWrapper>
            </Stack>
            <Stack>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='flex-start'>
                <PriceInEuroLabel>{item.price}</PriceInEuroLabel>
                <SquareMetersLabel>
                  {item.living_area_total && Math.round(item.living_area_total)}
                </SquareMetersLabel>
              </Stack>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='flex-start'>
                <Caption>{item.price_label}</Caption>
                <Caption>
                  {t(
                    'houseDetailPage.sections.details.overview.labels.living_area_total'
                  )}
                </Caption>
              </Stack>
            </Stack>
          </Stack>
        </StyledCardContent>
      </StyledCard>
    </LinkWrapperWithWT>
  )
})

HouseCard.displayName = 'HouseCard'
